import * as React from 'react';
import { createSelector } from '@reduxjs/toolkit';
import {
  UserCompanySelector,
  UserCompanySelectorOption,
} from 'copilot-design-system';
import { useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store';
import { selectActiveCompaniesMap } from 'src/store/clients/types';
import { zIndex } from 'src/theme/zindex';

type RecipientType = 'client' | 'company' | 'internalUser';

export const selectRecipientComboData = createSelector(
  [
    (state: RootState) => state.clients.activeClients,
    selectActiveCompaniesMap,
    (_, recipientTypes: RecipientType[]) => recipientTypes,
  ],
  (clients, companiesMap, recipientTypes) => {
    const clientOptions: UserCompanySelectorOption[] = [];
    const companyOptions: UserCompanySelectorOption[] = [];

    if (recipientTypes.includes('client')) {
      clients.forEach((c) => {
        c.fields.companyIds?.forEach((companyId) => {
          const company = companiesMap.get(companyId);
          if (company) {
            clientOptions.push({
              value: c.id,
              label: `${c.fields.givenName} ${c.fields.familyName}`,
              companyId: company.id,
              type: 'client',
              avatarSrc: c.fields.avatarImageUrl,
              avatarFallbackColor: c.fields.fallbackColor,
            });
          }
        });
      });

      companiesMap.forEach((company) => {
        if (company.fields.isPlaceholder) {
          return;
        }

        companyOptions.push({
          value: company.id,
          label: company.fields.name,
          type: 'company',
          companyId: company.id,
          avatarSrc: company.fields.avatarImageURL,
          avatarFallbackColor: company.fields.fallbackColor,
        });
      });
    }

    let internalUserOptions: UserCompanySelectorOption[] | undefined =
      undefined;

    if (recipientTypes.includes('internalUser')) {
      // TODO: Add internal user options
    }
    return {
      clientOptions,
      companyOptions,
      internalUserOptions,
    };
  },
);

export const RecipientSelector = ({
  recipientTypes,
  onRecipientSelected,
  name,
  ignoreCompanies = true,
  placeholder = '',
}: {
  recipientTypes: RecipientType[];
  onRecipientSelected: React.ComponentProps<
    typeof UserCompanySelector
  >['onChange'];
  name: string;
  ignoreCompanies?: boolean;
  placeholder?: string;
}) => {
  const { clientOptions, companyOptions, internalUserOptions } = useAppSelector(
    (state) => selectRecipientComboData(state, recipientTypes),
  );

  const isGrouped = recipientTypes.length > 1;

  return (
    <UserCompanySelector
      grouped={isGrouped}
      ignoreCompanies={ignoreCompanies}
      clientUsers={clientOptions}
      companies={companyOptions}
      internalUsers={internalUserOptions}
      placeholder={placeholder}
      name={name}
      onChange={onRecipientSelected}
      // this is needed for the menu to be inserted at the body in the html tree to avoid z-index issues.
      // E.g. if this selector is used in a modal, the menu would otherwise be inserted in the modal's
      // root which has a lower z-index than the modal itself and thus the menu would expand the height
      // of the modal instead of opening over it.
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: zIndex.modal }),
      }}
    />
  );
};
